<!-- 店铺保证金 / 首笔推广金 缴纳弹窗 -->
<template>
  <el-dialog
    :visible.sync="innerVisible"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    custom-class="deposit-promo-dialog"
    :width="view === 'payment' ? '520px' : '760px'"
    append-to-body
  >
    <div v-loading="pageLoading" class="dp-body">
      <!-- ============ 提醒 / 结果页（图1、图4、图5、图6） ============ -->
      <template v-if="view === 'reminder'">
        <div class="dp-header">
          <div class="dp-title">
            <span class="dp-flag">🏮</span>
            店铺费用缴纳提醒
          </div>
        </div>

        <!-- 香港公司/USD 链路：展示实时汇率 -->
        <div v-if="isUSD" class="dp-rate">
          💱 实时汇率：<b>{{ rateText }}</b>
          <span class="dp-rate-time">（取自 {{ rateTime }}）</span>
        </div>

        <!-- 顶部横幅：单项不足 / 审核不通过 -->
        <div v-if="bannerType === 'partial'" class="dp-banner dp-banner--warning">
          ⚠ 单项不足：{{ rejectedNames }}审核未通过，请根据下方原因重新提交凭证。
        </div>
        <div v-else-if="bannerType === 'rejected'" class="dp-banner dp-banner--error">
          ✕ 审核未通过：请确认金额与转账信息无误后重新上传凭证。
        </div>

        <div class="dp-desc">
          您的店铺<b>尚未完成费用缴纳</b>，请完成以下费用缴纳后即可开始经营：
        </div>

        <div class="dp-cards">
          <!-- 保证金卡片 -->
          <div v-if="deposit" class="dp-card">
            <div class="dp-card-name">店铺保证金</div>
            <div class="dp-card-amount" :class="statusColorClass(deposit.status)">
              {{ currencySymbol }}{{ formatAmount(deposit.expectedAmount) }}
            </div>
            <div class="dp-card-sub">
              基础保证金 {{ currencySymbol }}{{ formatAmount(deposit.baseAmount) }}
            </div>
            <div v-if="Number(deposit.specialAmount)" class="dp-card-sub">
              特殊品类保证金 {{ currencySymbol }}{{ formatAmount(deposit.specialAmount) }}
            </div>
            <div class="dp-card-status">
              <span class="dp-dot" :class="statusDotClass(deposit.status)" />
              {{ statusText(deposit.status) }}
            </div>
            <div v-if="deposit.status === 4 && deposit.rejectReason" class="dp-card-reason">
              原因：{{ deposit.rejectReason }}
            </div>
          </div>

          <!-- 推广金卡片 -->
          <div v-if="promoFund" class="dp-card">
            <div class="dp-card-name">首笔推广金</div>
            <div class="dp-card-amount" :class="statusColorClass(promoFund.status)">
              {{ currencySymbol }}{{ formatAmount(promoFund.expectedAmount) }}
            </div>
            <div class="dp-card-status">
              <span class="dp-dot" :class="statusDotClass(promoFund.status)" />
              {{ statusText(promoFund.status) }}
            </div>
            <div v-if="promoFund.status === 4 && promoFund.rejectReason" class="dp-card-reason">
              原因：{{ promoFund.rejectReason }}
            </div>
          </div>
        </div>

        <!-- 单项操作按钮 -->
        <div class="dp-card-actions">
          <el-button
            v-if="deposit"
            class="dp-item-btn"
            type="danger"
            :disabled="!isPayable(deposit.status)"
            @click="goPay([1])"
          >
            {{ deposit.status === 4 ? '重新上传' : '保证金缴纳上传凭证' }}
          </el-button>
          <el-button
            v-if="promoFund"
            class="dp-item-btn"
            type="danger"
            :disabled="!isPayable(promoFund.status)"
            @click="goPay([2])"
          >
            {{ promoFund.status === 4 ? '重新上传' : '推广金缴纳上传凭证' }}
          </el-button>
        </div>

        <!-- 合计 / 完成 按钮 -->
        <div class="dp-total-action">
          <el-button
            v-if="payableFundTypes.length"
            class="dp-total-btn"
            @click="goPay(payableFundTypes)"
          >
            {{ totalButtonText }}
          </el-button>
          <el-button
            v-else
            class="dp-total-btn"
            :disabled="hasPending"
            @click="onFinish"
          >
            已完成
          </el-button>
        </div>

        <!-- 模拟审核操作（演示用） -->
        <!-- <div v-if="showSimPanel" class="dp-sim">
          <div class="dp-sim-title">🖊 模拟审核操作（演示用）</div>
          <div class="dp-sim-btns">
            <span class="dp-sim-btn dp-sim-btn--pass" @click="simulateAudit('pass')">✅ 审核通过</span>
            <span class="dp-sim-btn dp-sim-btn--partial" @click="simulateAudit('partial')">⚠️ 单项不足</span>
            <span class="dp-sim-btn dp-sim-btn--reject" @click="simulateAudit('reject')">❌ 审核不通过</span>
          </div>
        </div> -->
      </template>

      <!-- ============ 缴纳开店费用页（图2、图3） ============ -->
      <template v-else>
        <div class="dp-pay-header">缴纳开店费用</div>

        <div class="dp-pay-total-label">应缴总计</div>
        <div class="dp-pay-total">{{ currencySymbol }}{{ formatAmount(payTotal) }}</div>

        <div class="dp-pay-breakdown">
          <template v-if="payTargets.includes(1) && deposit">
            <div class="dp-bd-row">
              <span>基础保证金</span>
              <span>{{ currencySymbol }}{{ formatAmount(deposit.baseAmount) }}</span>
            </div>
            <div v-if="Number(deposit.specialAmount)" class="dp-bd-row">
              <span>特殊品类保证金</span>
              <span>{{ currencySymbol }}{{ formatAmount(deposit.specialAmount) }}</span>
            </div>
          </template>
          <div v-if="payTargets.includes(2) && promoFund" class="dp-bd-row">
            <span>首笔推广金</span>
            <span>{{ currencySymbol }}{{ formatAmount(promoFund.expectedAmount) }}</span>
          </div>
        </div>

        <!-- 支付方式 Tab -->
        <div class="dp-tabs">
          <div
            v-for="m in availableMethods"
            :key="m.code"
            class="dp-tab"
            :class="{ 'is-active': paymentMethod === m.code }"
            @click="paymentMethod = m.code"
          >
            {{ m.name }}
          </div>
        </div>

        <!-- 银行转账 -->
        <div v-if="paymentMethod === 1" class="dp-bank">
          <div v-if="bankTransferInfo" class="dp-bank-box">
            <div class="dp-bank-row">
              <span class="dp-bank-label">收款账户</span>
              <span class="dp-bank-value">{{ bankTransferInfo.accountName }}</span>
              <span class="dp-copy" @click="copy(bankTransferInfo.accountName)">复制</span>
            </div>
            <div v-if="bankTransferInfo.companyAddress" class="dp-bank-row">
              <span class="dp-bank-label">公司地址</span>
              <span class="dp-bank-value">{{ bankTransferInfo.companyAddress }}</span>
              <span class="dp-copy" @click="copy(bankTransferInfo.companyAddress)">复制</span>
            </div>
            <div class="dp-bank-row">
              <span class="dp-bank-label">开户银行</span>
              <span class="dp-bank-value">{{ bankTransferInfo.bankName }}</span>
              <span class="dp-copy" @click="copy(bankTransferInfo.bankName)">复制</span>
            </div>
            <div v-if="bankTransferInfo.bankAddress" class="dp-bank-row">
              <span class="dp-bank-label">银行地址</span>
              <span class="dp-bank-value">{{ bankTransferInfo.bankAddress }}</span>
              <span class="dp-copy" @click="copy(bankTransferInfo.bankAddress)">复制</span>
            </div>
            <div class="dp-bank-row">
              <span class="dp-bank-label">收款账号</span>
              <span class="dp-bank-value">{{ bankTransferInfo.bankAccount }}</span>
              <span class="dp-copy" @click="copy(bankTransferInfo.bankAccount)">复制</span>
            </div>
            <div v-if="bankTransferInfo.swiftCode" class="dp-bank-row">
              <span class="dp-bank-label">SWIFT</span>
              <span class="dp-bank-value">{{ bankTransferInfo.swiftCode }}</span>
              <span class="dp-copy" @click="copy(bankTransferInfo.swiftCode)">复制</span>
            </div>
            <div class="dp-bank-row">
              <span class="dp-bank-label">转账附言</span>
              <span class="dp-bank-value">{{ transferRemark }}</span>
              <span class="dp-copy" @click="copy(transferRemark)">复制</span>
            </div>
          </div>
        </div>

        <!-- 支付宝 -->
        <div v-else class="dp-alipay">
          <img v-if="alipayInfo" class="dp-qr" :src="alipayInfo.qrCodeUrl">
          <div class="dp-qr-tip">请使用法人支付宝 App 扫一扫</div>
          <ul class="dp-tips">
            <li v-for="(tip, i) in alipayTips" :key="i" class="dp-tip">
              <span class="dp-tip-badge">{{ i + 1 }}</span>
              <span class="dp-tip-text">{{ tip }}</span>
            </li>
          </ul>
        </div>

        <!-- 上传凭证 -->
        <el-upload
          class="dp-upload"
          drag
          action="#"
          :accept="'.jpg,.jpeg,.png,.pdf'"
          :auto-upload="false"
          :show-file-list="true"
          :file-list="uploadFileList"
          :limit="3"
          multiple
          :on-change="onFileChange"
          :on-remove="onFileRemove"
          :on-exceed="onExceed"
        >
          <i class="el-icon-paperclip dp-upload-icon" />
          <div class="dp-upload-text">
            点击上传{{ paymentMethod === 1 ? '转账' : '支付宝支付' }}凭证
          </div>
          <div class="dp-upload-hint">支持 JPG / PNG / PDF，单文件 ≤ 5MB</div>
        </el-upload>

        <div class="dp-bank-warning">
          ⚠ 请确保转账金额与应缴金额一致。凭证提交后财务将进行审核。
        </div>

        <div class="dp-pay-footer">
          <el-button @click="view = 'reminder'">返回</el-button>
          <el-button
            type="danger"
            :loading="submitting"
            :disabled="!voucherKeys.length"
            @click="onSubmit"
          >
            {{ payTargets.length > 1 ? '提交（合并支付）' : '提交' }}
          </el-button>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import {
  getDepositPromoDetail,
  submitDepositPromo
} from '@/assets/api/deposit-promo'
import ApiConfig from '@/server-config/domain.config'

const STATUS_TEXT = {
  1: '待缴纳',
  2: '已提交，待审核',
  3: '已缴纳（审核通过）',
  4: '审核未通过'
}

export default {
  name: 'DepositPromoDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // 首页已拉取的 detail 数据，可直接传入，避免重复请求
    detailData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      detail: null,
      view: 'reminder', // reminder | payment
      payTargets: [], // 当前缴费页正在支付的资金类型
      paymentMethod: 1,
      uploadFileList: [], // { name, url, key } key 为临时文件 key
      regionMainland: true // 大陆公司 / 香港公司（依据 currency 字段初始化，可演示切换）
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    deposit() {
      return this.detail && this.detail.deposit
    },
    promoFund() {
      return this.detail && this.detail.promoFund
    },
    bankTransferInfo() {
      return this.detail && this.detail.bankTransferInfo
    },
    alipayInfo() {
      return this.detail && this.detail.alipayInfo
    },
    // 后端返回的原始币种
    baseCurrency() {
      return (this.deposit && this.deposit.currency) || 'CNY'
    },
    // 当前展示币种：大陆公司 CNY，香港公司 USD
    effectiveCurrency() {
      return this.regionMainland ? 'CNY' : 'USD'
    },
    isUSD() {
      return this.effectiveCurrency === 'USD'
    },
    // 后端返回的展示汇率对象
    exchangeRate() {
      return (this.detail && this.detail.displayExchangeRate) || null
    },
    // 1 USD 对应的 CNY 数值（quoteAmount / baseAmount），无数据时回退 7.25
    usdCnyRate() {
      const r = this.exchangeRate
      if (r && Number(r.baseAmount) && Number(r.quoteAmount)) {
        return Number(r.quoteAmount) / Number(r.baseAmount)
      }
      return 7.25
    },
    // 汇率展示文案：如「1 USD ≈ 7.25 CNY」
    rateText() {
      const r = this.exchangeRate
      if (r && r.baseCode && r.quoteCode) {
        return `${r.baseAmount} ${r.baseCode} ≈ ${r.quoteAmount} ${r.quoteCode}`
      }
      return `1 USD ≈ ${this.usdCnyRate} CNY`
    },
    // 汇率取值时间：优先取后端 displayExchangeRate.updatedAt（UTC），无则用当前时间
    rateTime() {
      const r = this.exchangeRate
      if (r && r.updatedAt) {
        return this.$tz.zeroToLocalZonetime(r.updatedAt, {
          format: 'yyyy-MM-dd hh:mm:ss'
        })
      }
      return this.formatNow()
    },
    currencySymbol() {
      return this.isUSD ? '$' : '¥'
    },
    availableMethods() {
      let list = (this.detail && this.detail.availablePaymentMethods) || []
      // 演示：香港公司/USD 链路仅银行转账，无支付宝
      if (!this.regionMainland) {
        list = list.filter(m => m.code !== 2)
      }
      return list
    },
    alipayTips() {
      // 支付宝缴费提示：先固定文案
      return [
        '打开法人支付宝 App 扫一扫，若不是法人支付宝支付，将退款且审核不通过',
        '输入对应金额进行缴费，支付成功后保留支付凭证',
        '支付凭证在下方上传点击提交等待官方财务审核'
      ]
    },
    // 可缴费（待缴纳 或 已拒绝）的资金类型
    payableFundTypes() {
      const arr = []
      if (this.deposit && this.isPayable(this.deposit.status)) {
        arr.push(1)
      }
      if (this.promoFund && this.isPayable(this.promoFund.status)) {
        arr.push(2)
      }
      return arr
    },
    // 是否存在待审核的项
    hasPending() {
      return (
        (this.deposit && this.deposit.status === 2)
        || (this.promoFund && this.promoFund.status === 2)
      )
    },
    // 顶部横幅类型
    bannerType() {
      const dr = this.deposit && this.deposit.status === 4
      const pr = this.promoFund && this.promoFund.status === 4
      const dp = this.deposit && this.deposit.status === 3
      const pp = this.promoFund && this.promoFund.status === 3
      if ((dr && pr) || (dr && !this.promoFund) || (pr && !this.deposit)) {
        return 'rejected'
      }
      if ((dr && pp) || (pr && dp)) {
        return 'partial'
      }
      return ''
    },
    rejectedNames() {
      const names = []
      if (this.deposit && this.deposit.status === 4) {
        names.push('保证金')
      }
      if (this.promoFund && this.promoFund.status === 4) {
        names.push('推广金')
      }
      return names.join('、')
    },
    // 是否展示演示面板（提交过至少一项后展示）
    showSimPanel() {
      return (
        (this.deposit && this.deposit.status !== 1)
        || (this.promoFund && this.promoFund.status !== 1)
      )
    },
    // 合计按钮文案
    totalButtonText() {
      const allRejected = this.payableFundTypes.every(t => {
        const item = t === 1 ? this.deposit : this.promoFund
        return item && item.status === 4
      })
      if (this.payableFundTypes.length > 1 && allRejected) {
        return '重新上传合计支付凭证'
      }
      const total = this.payableFundTypes.reduce((sum, t) => {
        const item = t === 1 ? this.deposit : this.promoFund
        return sum + Number((item && item.expectedAmount) || 0)
      }, 0)
      return `合计支付 (${this.currencySymbol}${this.formatAmount(total)})`
    },
    // 缴费页应缴总计
    payTotal() {
      return this.payTargets.reduce((sum, t) => {
        const item = t === 1 ? this.deposit : this.promoFund
        return sum + Number((item && item.expectedAmount) || 0)
      }, 0)
    },
    transferRemark() {
      const names = this.payTargets.map(t => (t === 1 ? '保证金' : '推广金'))
      const promoStoreId = this.promoFund && this.promoFund.storeId
      const depositStoreId = this.deposit && this.deposit.storeId
      const storeId = promoStoreId || depositStoreId || ''
      return `${names.join(' + ')}（${storeId}）`
    },
    voucherKeys() {
      return this.uploadFileList.map(f => f.key).filter(Boolean)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.view = 'reminder'
        if (this.detailData) {
          this.detail = JSON.parse(JSON.stringify(this.detailData))
          this.initRegion()
        } else {
          this.loadDetail()
        }
      }
    },
    detailData: {
      handler(val) {
        if (val) {
          this.detail = JSON.parse(JSON.stringify(val))
          this.initRegion()
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadDetail() {
      this.pageLoading = true
      try {
        const res = await getDepositPromoDetail(this.$axios)
        if (res.code === 0) {
          this.detail = res.data
          this.initRegion()
        } else {
          this.$message.error(res.msg || '获取缴费信息失败')
        }
      } catch (e) {
        console.error(e)
        this.$handleError(this, e)
      } finally {
        this.pageLoading = false
      }
    },
    // 依据后端 currency 字段初始化地区：USD 视为香港公司
    initRegion() {
      this.regionMainland = this.baseCurrency !== 'USD'
    },
    formatNow() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      const date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
      const time = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
      return `${date} ${time}`
    },
    statusText(status) {
      return STATUS_TEXT[status] || ''
    },
    statusColorClass(status) {
      return {
        'is-pending': status === 1,
        'is-audit': status === 2,
        'is-pass': status === 3,
        'is-reject': status === 4
      }
    },
    statusDotClass(status) {
      return {
        'dot-red': status === 1 || status === 4,
        'dot-yellow': status === 2,
        'dot-green': status === 3
      }
    },
    isPayable(status) {
      return status === 1 || status === 4
    },
    // 依据当前展示币种换算金额（后端金额币种为 baseCurrency）
    convertAmount(v) {
      const amount = Number(v || 0)
      if (this.baseCurrency === this.effectiveCurrency) {
        return amount
      }
      if (this.baseCurrency === 'CNY' && this.effectiveCurrency === 'USD') {
        return amount / this.usdCnyRate
      }
      if (this.baseCurrency === 'USD' && this.effectiveCurrency === 'CNY') {
        return amount * this.usdCnyRate
      }
      return amount
    },
    formatAmount(v) {
      return this.convertAmount(v).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    goPay(fundTypes) {
      this.payTargets = [...fundTypes]
      this.uploadFileList = []
      const firstMethod = this.availableMethods[0]
      this.paymentMethod = (firstMethod && firstMethod.code) || 1
      this.view = 'payment'
    },
    copy(text) {
      this.$copyText(String(text || '')).then(() => {
        this.$message.success('已复制')
      })
    },
    async onFileChange(file) {
      const raw = file.raw
      if (!raw) {
        return
      }
      // 同一文件 on-change 可能触发多次，已处理则跳过
      if (this.uploadFileList.some(f => f.uid === file.uid)) {
        return
      }
      if (this.uploadFileList.length >= 3) {
        this.$message.warning('最多上传 3 个凭证文件')
        return
      }
      if (raw.size > 5 * 1024 * 1024) {
        this.$message.warning('单文件不能超过 5MB')
        return
      }
      const typeObj = { image: '1', video: '2', text: '3' }
      const matchedType = ['image', 'video'].find(t => raw.type.includes(t)) || 'text'
      const fType = typeObj[matchedType]
      const ext = raw.name.split('.').pop()
      const randStr = Math.random().toString(36)
      const rand = randStr.slice(2, 8)
      const fileName = `${Date.now()}_${rand}.${ext}`
      try {
        // 先获取预签名上传地址和临时文件 key，此时不真正上传文件
        const { filename, url } = await this.$axios
          .post('/upload-file-service', {
            service_name: 'store-fund-voucher',
            file_type: fType,
            file_size: (raw.size / 1024 / 1024).toFixed(4),
            filename: fileName
          })
          .then(r => r.data.data)
        if (!url) {
          this.$message.error('获取上传地址失败')
          return
        }
        if (this.uploadFileList.length >= 3) {
          return
        }
        // 暂存文件与预签名地址，等最终提交时再 PUT 上传
        this.uploadFileList.push({
          uid: file.uid,
          name: raw.name,
          url: `${ApiConfig.HW_OBS}/${filename}`,
          raw,
          putUrl: url,
          key: filename
        })
      } catch (e) {
        console.error(e)
        this.$handleError(this, e)
      }
    },
    onFileRemove(file) {
      this.uploadFileList = this.uploadFileList.filter(f => f.uid !== file.uid)
    },
    // 将单个凭证文件 PUT 上传到其预签名地址
    putVoucher(f) {
      if (!f.raw || !f.putUrl) {
        return Promise.resolve()
      }
      return this.$axios({
        method: 'PUT',
        url: f.putUrl,
        data: f.raw,
        headers: { accept: '*/*', 'Content-Type': f.raw.type },
        withCredentials: 0
      })
    },
    onExceed() {
      this.$message.warning('最多上传 3 个凭证文件')
    },
    async onSubmit() {
      if (!this.uploadFileList.length) {
        this.$message.warning('请先上传付款凭证')
        return
      }
      this.submitting = true
      try {
        // 最终提交时，才把已选择的文件真正 PUT 上传到预签名地址
        await Promise.all(this.uploadFileList.map(f => this.putVoucher(f)))
        const res = await submitDepositPromo(this.$axios, {
          fundTypes: this.payTargets,
          paymentMethod: this.paymentMethod,
          voucherFiles: this.voucherKeys
        })
        if (res.code === 0) {
          this.$message.success('提交成功，等待财务审核')
          this.detail = res.data
          this.uploadFileList = []
          this.view = 'reminder'
        } else {
          this.$message.error(res.msg || '提交失败')
        }
      } catch (e) {
        console.error(e)
        this.$handleError(this, e)
      } finally {
        this.submitting = false
      }
    },
    onFinish() {
      // this.innerVisible = false
      // this.$emit('completed')
    },
    // 演示用：本地模拟审核结果
    simulateAudit(type) {
      if (!this.detail) {
        return
      }
      if (type === 'pass') {
        if (this.deposit) {
          this.deposit.status = 3
          this.deposit.rejectReason = null
        }
        if (this.promoFund) {
          this.promoFund.status = 3
          this.promoFund.rejectReason = null
        }
        this.$message.success('演示：全部审核通过')
      } else if (type === 'partial') {
        if (this.deposit) {
          this.deposit.status = 3
          this.deposit.rejectReason = null
        }
        if (this.promoFund) {
          this.promoFund.status = 4
          this.promoFund.rejectReason = '支付凭证与实际金额不符，请重新提交'
        }
      } else if (type === 'reject') {
        if (this.deposit) {
          this.deposit.status = 4
          this.deposit.rejectReason = '支付凭证与实际金额不符，请重新提交'
        }
        if (this.promoFund) {
          this.promoFund.status = 4
          this.promoFund.rejectReason = '支付凭证与实际金额不符，请重新提交'
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.deposit-promo-dialog {
  .dp-body {
    min-height: 120px;
  }
}
/* ---------- 提醒页 ---------- */
.dp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}
.dp-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  .dp-flag {
    margin-right: 8px;
  }
}
.dp-region {
  margin-left: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #e4393c;
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 16px;
  padding: 2px 12px;
  cursor: pointer;
  user-select: none;
}
.dp-rate {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 14px;
  background: #e6f4ff;
  border: 1px solid #91caff;
  color: #0958d9;
  b {
    font-weight: 600;
  }
  .dp-rate-time {
    color: #69a0e0;
    font-weight: 400;
    margin-left: 4px;
  }
}
.dp-banner {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 14px;
  &--warning {
    background: #fffbe6;
    border: 1px solid #ffe58f;
    color: #ad6800;
  }
  &--error {
    background: #fff1f0;
    border: 1px solid #ffccc7;
    color: #cf1322;
  }
}
.dp-desc {
  font-size: 15px;
  color: #666;
  margin-bottom: 18px;
  b {
    color: #333;
  }
}
.dp-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 18px;
}
.dp-card {
  flex: 1;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 18px 20px;
  .dp-card-name {
    font-size: 15px;
    color: #666;
    margin-bottom: 8px;
  }
  .dp-card-amount {
    font-size: 30px;
    font-weight: 700;
    margin-bottom: 8px;
    &.is-pending,
    &.is-reject {
      color: #e4393c;
    }
    &.is-audit {
      color: #fa8c16;
    }
    &.is-pass {
      color: #52c41a;
    }
  }
  .dp-card-sub {
    font-size: 13px;
    color: #999;
    line-height: 1.6;
  }
  .dp-card-status {
    margin-top: 10px;
    font-size: 14px;
    color: #666;
    display: flex;
    align-items: center;
  }
  .dp-card-reason {
    margin-top: 6px;
    font-size: 13px;
    color: #cf1322;
  }
}
.dp-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  &.dot-red {
    background: #e4393c;
  }
  &.dot-yellow {
    background: #fadb14;
  }
  &.dot-green {
    background: #52c41a;
  }
}
.dp-card-actions {
  display: flex;
  gap: 20px;
  margin-bottom: 14px;
  .dp-item-btn {
    flex: 1;
    height: 48px;
    font-size: 16px;
  }
}
.dp-total-action {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
  .dp-total-btn {
    min-width: 260px;
    height: 48px;
    font-size: 16px;
    color: #fff;
    background: #faad14;
    border-color: #faad14;
    &.is-disabled {
      background: #ffd591;
      border-color: #ffd591;
    }
  }
}
.dp-sim {
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #d6e4ff;
  border-radius: 8px;
  background: #f0f5ff;
  text-align: center;
  .dp-sim-title {
    font-size: 13px;
    color: #666;
    margin-bottom: 10px;
  }
  .dp-sim-btns {
    display: flex;
    justify-content: center;
    gap: 12px;
  }
  .dp-sim-btn {
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    border: 1px solid transparent;
    &--pass {
      background: #f6ffed;
      border-color: #b7eb8f;
      color: #389e0d;
    }
    &--partial {
      background: #fffbe6;
      border-color: #ffe58f;
      color: #ad6800;
    }
    &--reject {
      background: #fff1f0;
      border-color: #ffccc7;
      color: #cf1322;
    }
  }
}
/* ---------- 缴费页 ---------- */
.dp-pay-header {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}
.dp-pay-total-label {
  font-size: 13px;
  color: #999;
}
.dp-pay-total {
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 14px;
}
.dp-pay-breakdown {
  background: #fafafa;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  .dp-bd-row {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    color: #666;
    padding: 4px 0;
  }
}
.dp-tabs {
  display: flex;
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
  .dp-tab {
    flex: 1;
    text-align: center;
    padding: 12px 0;
    font-size: 15px;
    color: #666;
    cursor: pointer;
    &.is-active {
      color: #e4393c;
      font-weight: 600;
      box-shadow: inset 0 -2px 0 #e4393c;
    }
  }
}
.dp-bank-box {
  background: #fafafa;
  border-radius: 8px;
  padding: 6px 16px;
  margin-bottom: 16px;
}
.dp-bank-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  font-size: 14px;
  border-bottom: 1px dashed #eee;
  &:last-child {
    border-bottom: none;
  }
  .dp-bank-label {
    width: 80px;
    color: #999;
    flex-shrink: 0;
  }
  .dp-bank-value {
    flex: 1;
    color: #333;
    word-break: break-all;
  }
  .dp-copy {
    color: #666;
    cursor: pointer;
    padding: 2px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 12px;
    &:hover {
      color: #e4393c;
      border-color: #e4393c;
    }
  }
}
.dp-alipay {
  text-align: center;
  margin-bottom: 16px;
  .dp-qr {
    width: 180px;
    height: 180px;
    object-fit: contain;
  }
  .dp-qr-tip {
    font-size: 13px;
    color: #666;
    margin: 8px 0 12px;
  }
  .dp-tips {
    text-align: left;
    list-style: none;
    padding: 0;
    margin: 0;
    .dp-tip {
      display: flex;
      align-items: flex-start;
      margin-bottom: 12px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    .dp-tip-badge {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      margin-right: 10px;
      border-radius: 20px;
      background: #e4393c;
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      line-height: 20px;
      text-align: center;
    }
    .dp-tip-text {
      flex: 1;
      font-size: 13px;
      color: #999;
      line-height: 20px;
    }
  }
}
.dp-upload {
  margin-bottom: 12px;
  ::v-deep .el-upload--text {
    text-align: center;
    width: 100%;
  }
  ::v-deep .el-upload-dragger {
    width: 100%;
    height: auto;
    padding: 24px 0;
  }
  .dp-upload-icon {
    font-size: 28px;
    color: #bbb;
  }
  .dp-upload-text {
    font-size: 14px;
    color: #666;
    margin-top: 8px;
  }
  .dp-upload-hint {
    font-size: 12px;
    color: #bbb;
    margin-top: 4px;
  }
}
.dp-bank-warning {
  font-size: 13px;
  color: #ad6800;
  margin-bottom: 12px;
}
.dp-pay-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
}
</style>

<!-- 浮层渲染在 body 上，scoped 样式无法命中，这里用 custom-class 限定作用域 -->
<style lang="scss">
.deposit-promo-dialog {
  .el-dialog__header {
    display: none;
  }
}
</style>
